/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useSelector } from '@xstate/react';
import { InferGetServerSidePropsType } from 'next';
import { useCallback, useEffect } from 'react';

import { Spinner } from '@src/components/atoms/spinner';
import { ContentWithLink } from '@src/components/common/formatting/content-with-link';
import { useBrand } from '@src/contexts/brand-context';
import { CmsContent, useCmsContent } from '@src/contexts/cms-content-context';
import { useToast } from '@src/contexts/toast-context';
import { AuthLayout } from '@src/features/auth/components/auth-layout';
import { ConfirmSignUp } from '@src/features/auth/components/forms/confirm-sign-up';
import { ForgotPasswordForm } from '@src/features/auth/components/forms/forgot-password-form';
import { LoginForm } from '@src/features/auth/components/forms/login-form';
import { OverviewForm } from '@src/features/auth/components/forms/overview-form';
import { ResetPasswordForm } from '@src/features/auth/components/forms/reset-password-form';
import { SignUpForm } from '@src/features/auth/components/forms/sign-up-form';
import { VerifyResetPassword } from '@src/features/auth/components/verify-reset-password';
import { useAuthMachine } from '@src/features/auth/hooks/use-auth-machine';
import { AuthException } from '@src/features/auth/services';
import { tags } from '@src/features/auth/state-machine/auth-machine';
import { withBrandData } from '@src/lib/ssr/with-brand-data';
import { withCommonData } from '@src/lib/ssr/with-common-data';
import { withLogger } from '@src/lib/ssr/with-logger';
import { getContentService } from '@src/services/graphql';
import { StringFields } from '@src/utils/types';

export const getServerSideProps = withLogger(
  withCommonData(
    withBrandData(async () => {
      const contentService = await getContentService();
      const { backgroundImage } = await contentService.getAuthPage();

      return {
        props: {
          backgroundImage: backgroundImage.responsiveImage,
        },
      };
    }),
  ),
);

export type AuthPageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
const errorMessages: Record<AuthException['code'], StringFields<CmsContent>> = {
  EmailAlreadyExists: 'errorEmailAlreadyExists',
  InvalidCode: 'errorInvalidCode',
  InvalidCredentials: 'errorInvalidCredentials',
  NotConfirmed: 'errorNotConfirmed',
  LimitExceeded: 'errorInvalidCredentials',
};

const AuthPage = ({ backgroundImage }: AuthPageProps) => {
  const { brand } = useBrand();
  const content = useCmsContent();

  const {
    machine: [state, send, service],
    isReady,
  } = useAuthMachine(brand);

  const { addToast } = useToast();
  const error = useSelector(service, ({ context }) => context.error);

  const resendVerification = useCallback(
    () =>
      send({
        type: 'RESEND_VERIFICATION',
      }),
    [send],
  );

  useEffect(() => {
    if (error) {
      send('RESET_ERROR');

      if (error === 'InvalidCode') {
        addToast({
          type: 'error',
          title: content.error,
          content: <ContentWithLink contentKey="expiredLink" onClick={resendVerification} />,
        });
        return;
      }

      addToast({
        type: 'error',
        title: content.error,
        content: content[errorMessages[error]],
      });
    }
  }, [addToast, content, error, resendVerification, send]);

  if (
    !isReady ||
    state.matches('authenticated') ||
    state.matches({ 'sign-up': 'checking-verification' }) ||
    state.matches({ 'sign-up': 'auto-login' }) ||
    state.context.isRedirecting
  ) {
    return (
      <AuthLayout.Main>
        <Spinner />
      </AuthLayout.Main>
    );
  }

  return (
    <AuthLayout.Main background={backgroundImage}>
      {state.matches('overview') && (
        <OverviewForm
          email={state.context.email}
          isLoading={state.hasTag(tags.LOADING)}
          socialProvider={state.context.provider}
          onSubmit={(email) => send({ type: 'SUBMIT_EMAIL', email })}
          onSocialLogin={(provider) =>
            send({
              type: 'SOCIAL_LOGIN',
              provider,
            })
          }
        />
      )}

      {state.matches('login') && (
        <LoginForm
          email={state.context.email!}
          isLoading={state.hasTag(tags.LOADING)}
          onLogin={(email, password) =>
            send({
              type: 'LOGIN',
              email,
              password,
            })
          }
          onForgotPassword={(email) =>
            send({
              type: 'FORGOT_PASSWORD',
              email,
            })
          }
        />
      )}

      {(state.matches({ 'sign-up': 'enter-data' }) || state.matches({ 'sign-up': 'loading' })) && (
        <SignUpForm
          fullName={state.context.fullName}
          email={state.context.email!}
          isLoading={state.hasTag(tags.LOADING)}
          onSignUp={(data) => {
            send({
              type: 'SIGN_UP',
              ...data,
            });
          }}
        />
      )}

      {(state.matches({ 'sign-up': 'verification' }) || state.matches({ 'sign-up': 'sending-verification' })) && (
        <ConfirmSignUp isLoading={state.hasTag(tags.LOADING)} onResendVerification={resendVerification} />
      )}

      {(state.matches({ 'forgot-password': 'enter-data' }) ||
        state.matches({ 'forgot-password': 'sending-verification' })) && (
        <ForgotPasswordForm
          email={state.context.email!}
          isLoading={state.hasTag(tags.LOADING)}
          onForgotPassword={(email) =>
            send({
              type: 'REQUEST_RESET',
              email,
            })
          }
        />
      )}

      {(state.matches({ 'forgot-password': 'verification' }) ||
        state.matches({ 'forgot-password': 'resending-verification' })) && (
        <VerifyResetPassword
          isLoading={state.hasTag(tags.LOADING)}
          onResendEmail={() =>
            send({
              type: 'REQUEST_RESET',
              email: state.context.email!,
            })
          }
        />
      )}

      {(state.matches({ 'forgot-password': 'change-password' }) ||
        state.matches({ 'forgot-password': 'submitting' })) && (
        <ResetPasswordForm
          email={state.context.email!}
          isLoading={state.hasTag(tags.LOADING)}
          verificationCode={state.context.verificationCode || ''}
          onResetPassword={(password) =>
            send({
              type: 'CHANGE_PASSWORD',
              password,
            })
          }
        />
      )}
    </AuthLayout.Main>
  );
};
AuthPage.Layout = AuthLayout;
export default AuthPage;
